import { FormField, ImageIcon, useModeContext } from 'canoe';
import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';

import { useToggle } from 'commons/hooks';
import { MediaLibrary } from 'craft/components/MediaLibrary';
import {
  StyledTextInput,
  StyledToggle,
} from 'craft/containers/Toolbox/components/StyledComponents';
import ValueGroup from 'craft/containers/Toolbox/components/ValueGroup';
import { CollectionTargetLocation, TargetLocationsEnglishNames } from 'craft/types';

import {
  FallbacksContainer,
  FallbacksToggle,
  FallbacksToggleWrapper,
  MediaValueBox,
} from './StyledComponents';
import { FallbacksEditorImplProps, VisibilityOptions } from './types';

export const FallbacksEditorToolbox = React.memo(
  ({
    collectionId,
    hasFallback,
    fieldData,
    fallbackMappings,
    onChangeVisibility,
    onSetImageFromLibrary,
    onBlur,
    nodeIds,
  }: FallbacksEditorImplProps) => {
    const { mode } = useModeContext();

    const [showFallbacks, onToggleFallbacks] = useToggle(hasFallback);
    const [fallbackValues, setFallbackValues] = useState<
      Record<CollectionTargetLocation, string | undefined>
    >(fallbackMappings);
    const [isMediaModalOpen, toggleMediaModal] = useToggle(false);

    const handleBlur = (targetLocation: CollectionTargetLocation) => {
      const newValue =
        targetLocation === CollectionTargetLocation.Text
          ? fallbackValues[targetLocation] || ''
          : fallbackValues[targetLocation]?.replace(/https?:\/\//, '') || '';
      setFallbackValues({
        ...fallbackValues,
        [targetLocation]: newValue,
      });
      onBlur(newValue, targetLocation);
    };

    const setImageURL = useCallback(
      (url, targetLocation) => {
        const cleanUrl = url.replace(/https?:\/\//, '');
        setFallbackValues({
          ...fallbackValues,
          [targetLocation]: cleanUrl,
        });
        onSetImageFromLibrary(targetLocation, `https://${cleanUrl}`);
      },
      [fallbackValues, onSetImageFromLibrary],
    );

    // Make sure the fallbacks visibility and values is updated when
    // switching components, as toolbox doesn't cause a re-render
    useEffect(() => {
      if (hasFallback !== showFallbacks) {
        onToggleFallbacks();
      }
      setFallbackValues(fallbackMappings);
    }, [hasFallback, fallbackMappings]);

    return (
      <FallbacksContainer>
        <ValueGroup>
          <FallbacksToggleWrapper dark label="Empty elements" isActive={showFallbacks} isFirst>
            <FallbacksToggle
              toggleGroup={`fallback-${collectionId}`}
              onClick={() => {
                onChangeVisibility(VisibilityOptions.HIDE);
                onToggleFallbacks();
              }}
              isActive={!showFallbacks}
              isFirst
              icon={() => <span>Hide</span>}
              responsiveWidth
              color="orange"
            />
            <FallbacksToggle
              toggleGroup={`fallback-${collectionId}`}
              onClick={() => {
                onChangeVisibility(VisibilityOptions.SHOW);
                onToggleFallbacks();
              }}
              isActive={showFallbacks}
              isFirst
              icon={() => <span>Fallback Value</span>}
              responsiveWidth
              color="orange"
            />
          </FallbacksToggleWrapper>
          {showFallbacks && (
            <FormField data-cy="fallback" scale="xsmall" label="">
              <>
                {fieldData.map(({ targetLocation }, index) => (
                  <div key={`fallback-wrapper-${targetLocation}`}>
                    {targetLocation === CollectionTargetLocation.Image && (
                      <>
                        <MediaValueBox dark label="Media Library">
                          <StyledToggle
                            id="toggle-media-library"
                            onClick={toggleMediaModal}
                            isActive
                            icon={ImageIcon}
                            color="orange"
                          />
                        </MediaValueBox>
                        <MediaLibrary
                          nodeIds={nodeIds}
                          name="src"
                          isModalOpen={isMediaModalOpen}
                          toggleModal={toggleMediaModal}
                          setImageURL={(url) => setImageURL(url, targetLocation)}
                        />
                      </>
                    )}

                    <StyledTextInput
                      name="fallbackValue-toolbox"
                      value={fallbackValues?.[targetLocation] || ''}
                      placeholder={
                        targetLocation === CollectionTargetLocation.Text
                          ? 'Fallback text in case of missing data'
                          : `${TargetLocationsEnglishNames[
                              targetLocation
                            ].toLowerCase()}-in-case-of-missing-data`
                      }
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setFallbackValues({
                          ...fallbackValues,
                          [targetLocation]: e.target.value,
                        })
                      }
                      onBlur={() => handleBlur(targetLocation)}
                      prefix={
                        targetLocation === CollectionTargetLocation.Text ? undefined : 'https://'
                      }
                      mode={mode}
                      isLast={index === fieldData.length - 1}
                      variant="collections"
                    />
                  </div>
                ))}
              </>
            </FormField>
          )}
        </ValueGroup>
      </FallbacksContainer>
    );
  },
);
